/**
 * Main layout component with navigation and header.
 *
 * Mobile-first: hamburger drawer below the `lg` breakpoint, horizontal nav at
 * `lg`+. The handover used to happen at `md` (768px), but the bar — the primary
 * links, the group dropdowns, the user's name and Logout — needs roughly
 * 1000px. At 768–1023px the links physically overlapped the profile/logout
 * button, making both unclickable on iPad portrait and on a phone held
 * sideways. `e2e` catches that as an `occlusion` finding. The bar carried five
 * primary links and one dropdown when that was measured; it now carries four
 * and two, which is marginally narrower — keep it that way.
 *
 * What renders where is decided entirely by `./nav.tsx`, including who may see
 * each item. This file renders; it does not gate.
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useAuth } from '../services/auth'
import { getAuditSignificantCount, getSecurityAlertCounts } from '../services/admin'
import { getConstructionNeedsContactCount } from '../services/licenses'
import api from '../services/api'
import {
  NAV_ABOUT,
  NAV_GROUPS,
  NAV_PRIMARY,
  NAV_USER,
  visibleGroups,
  visibleTo,
  type NavBadge,
  type NavGroup,
  type NavItem,
} from './nav'
import {
  ChevronDownIcon,
  HamburgerIcon,
  LogoutIcon,
  UserCircleIcon,
} from './navIcons'

export default function Layout() {
  const { user, logout } = useAuth()
  const location = useLocation()
  /** At most one group dropdown is open; `null` means none. */
  const [openGroupId, setOpenGroupId] = useState<string | null>(null)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [auditBadgeCount, setAuditBadgeCount] = useState(0)
  // Open critical/high security alerts: the red menu badge and the banner.
  const [alertCount, setAlertCount] = useState(0)
  const [constructionBadgeCount, setConstructionBadgeCount] = useState(0)
  const [environment, setEnvironment] = useState<string | null>(null)
  const [appVersion, setAppVersion] = useState('0.11.0')
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const groupBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const userMenuRef = useRef<HTMLDivElement>(null)
  const userMenuBtnRef = useRef<HTMLButtonElement>(null)
  const mobileMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    api
      .get<{ environment: string; app_version: string }>('/v1/version')
      .then(r => {
        setEnvironment(r.data.environment)
        setAppVersion(r.data.app_version)
      })
      .catch(() => {})
  }, [])

  const refreshBadge = useCallback(async () => {
    if (user?.is_superuser) {
      setAuditBadgeCount(await getAuditSignificantCount())
    }
    // Everyone can open Construction Deadlines, so everyone gets its count.
    if (user) setConstructionBadgeCount(await getConstructionNeedsContactCount())
  }, [user])

  useEffect(() => {
    refreshBadge()
    const interval = setInterval(refreshBadge, 5 * 60 * 1000) // refresh every 5 min
    return () => clearInterval(interval)
  }, [refreshBadge])

  // Alerts are re-read on every page change as well as on the timer: the banner is
  // the point of them, and an admin moving around the app should see one promptly
  // (and see it go once somebody acknowledges it).
  useEffect(() => {
    if (!user?.is_superuser) return
    getSecurityAlertCounts().then(c => setAlertCount(c.urgent)).catch(() => {})
  }, [user, location.pathname])

  const onPath = (path: string) =>
    path === '/'
      ? location.pathname === '/'
      : location.pathname === path || location.pathname.startsWith(path + '/')

  const isActive = (path: string) =>
    onPath(path)
      ? 'bg-primary-700 text-white'
      : 'text-gray-300 hover:bg-primary-600 hover:text-white'

  const isMobileActive = (path: string) =>
    onPath(path)
      ? 'bg-primary-700 text-white'
      : 'text-gray-200 hover:bg-primary-600 hover:text-white'

  const isDropdownItemActive = (path: string) =>
    onPath(path)
      ? 'text-primary-700 bg-primary-50 font-medium'
      : 'text-gray-700 hover:bg-gray-100'

  /**
   * Highlight a group trigger from what the group actually contains. This used
   * to be `pathname.startsWith('/admin/')`, which was only ever right while a
   * single dropdown owned every admin path — with Do Not Contact now under
   * Tools, that test would light up the wrong trigger.
   */
  const isGroupActive = (group: NavGroup) => group.items.some(i => onPath(i.to))

  const isAdmin = !!user?.is_superuser
  /** Employee-or-admin, matching the API's `_require_employee_or_admin`: a viewer
      is read-only and must not be shown pages whose whole purpose is editing. */
  const isEmployee = isAdmin || (user?.roles ?? []).includes('employee')
  /** The only visibility decision in this file, and it defers to nav.tsx. */
  const groups = visibleGroups(NAV_GROUPS, isAdmin, isEmployee)

  // One badge renderer, shared by both menus. The audit badge used to exist
  // only on desktop, so on a phone there was no signal that anything needed
  // attention — half of the bug this file's rewrite fixes.
  const badgeCount = (badge: NavBadge | undefined) =>
    badge === 'alerts' ? alertCount
      : badge === 'audit' ? auditBadgeCount
      : badge === 'construction' ? constructionBadgeCount : 0
  const badgeFor = (item: Pick<NavItem, 'badge'>, tone: 'light' | 'dark') => {
    const count = badgeCount(item.badge)
    if (count <= 0) return null
    // Red for the alarms (alerts, audit); amber for the construction to-do count.
    const colours = item.badge === 'audit' || item.badge === 'alerts'
      ? (tone === 'light' ? 'bg-red-100 text-red-700' : 'bg-red-500 text-white')
      : (tone === 'light' ? 'bg-amber-100 text-amber-800' : 'bg-amber-400 text-gray-900')
    return (
      <span
        className={`ml-2 px-1.5 py-0.5 text-xs font-semibold rounded-full ${colours}`}
        aria-label={item.badge === 'construction' ? `${count} need first contact` : undefined}
      >
        {count}
      </span>
    )
  }

  // Every item carries its own icon (a required field on NavItem), so all three
  // renderers show one — including the dropdown, which used to render the label
  // alone and so was the one place on desktop with no iconography at all.
  const renderTopLink = ({ icon: Icon, ...item }: NavItem) => (
    <Link
      key={item.to}
      to={item.to}
      className={`${isActive(item.to)} flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors`}
    >
      <Icon className="h-4 w-4 flex-shrink-0" />
      {item.label}
      {badgeFor(item, 'dark')}
    </Link>
  )

  const renderDropdownLink = ({ icon: Icon, ...item }: NavItem) => (
    <Link
      key={item.to}
      role="menuitem"
      to={item.to}
      className={`flex items-center gap-2.5 px-4 py-2 text-sm ${isDropdownItemActive(item.to)}`}
    >
      <Icon className="h-4 w-4 flex-shrink-0 text-gray-400" />
      <span className="flex-1">{item.label}</span>
      {badgeFor(item, 'light')}
    </Link>
  )

  const renderDrawerLink = ({ icon: Icon, ...item }: NavItem) => (
    <Link
      key={item.to}
      to={item.to}
      className={`${isMobileActive(item.to)} flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium transition-colors`}
    >
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span>{item.label}</span>
      {badgeFor(item, 'dark')}
    </Link>
  )

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node
      setOpenGroupId(id => (id && !groupRefs.current[id]?.contains(target) ? null : id))
      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close everything on navigation
  useEffect(() => {
    setOpenGroupId(null)
    setUserMenuOpen(false)
    setMobileMenuOpen(false)
  }, [location.pathname])

  // Close the open dropdown on Escape; return focus to its trigger
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (openGroupId) {
          const btn = groupBtnRefs.current[openGroupId]
          setOpenGroupId(null)
          btn?.focus()
        }
        if (userMenuOpen) {
          setUserMenuOpen(false)
          userMenuBtnRef.current?.focus()
        }
        if (mobileMenuOpen) {
          setMobileMenuOpen(false)
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [openGroupId, userMenuOpen, mobileMenuOpen])

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Skip navigation link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-white focus:text-primary-800 focus:rounded focus:shadow-lg focus:font-medium"
      >
        Skip to main content
      </a>

      {/* Navigation header */}
      <nav className="bg-primary-800 shadow-lg" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">

            {/* Logo */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center gap-2 text-white hover:text-white/90 transition-opacity">
                <img src="/favicon.svg" alt="" aria-hidden="true" className="h-8 w-8" />
                <span className="text-lg font-bold leading-tight">
                  <span className="hidden sm:inline">FCC License Manager</span>
                  <span className="sm:hidden">FCC Licenses</span>
                </span>
              </Link>
            </div>

            {/* Desktop nav — hidden on mobile. Rendered from ./nav.tsx, the
                same source the mobile drawer uses. */}
            <div className="hidden lg:flex items-center space-x-1 ml-6">
              {visibleTo(NAV_PRIMARY, isAdmin, isEmployee).map(renderTopLink)}

              {/* One dropdown per group. A group is here only if it has at
                  least one item this user may see, so an ordinary user gets
                  Tools (Construction Deadlines) and no Admin trigger at all. */}
              {groups.map(group => {
                const items = visibleTo(group.items, isAdmin, isEmployee)
                const open = openGroupId === group.id
                const GroupIcon = group.icon
                return (
                  <div
                    key={group.id}
                    className="relative"
                    ref={el => { groupRefs.current[group.id] = el }}
                  >
                    <button
                      ref={el => { groupBtnRefs.current[group.id] = el }}
                      onClick={() => setOpenGroupId(id => (id === group.id ? null : group.id))}
                      aria-expanded={open}
                      aria-haspopup="true"
                      aria-controls={group.id}
                      className={`${isGroupActive(group) ? 'bg-primary-700 text-white' : 'text-gray-300 hover:bg-primary-600 hover:text-white'} flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors`}
                    >
                      <GroupIcon className="h-4 w-4 flex-shrink-0" />
                      <span>{group.label}</span>
                      {/* Collapsed-state hint that something inside needs
                          attention — follows the badged item's group. */}
                      {(auditBadgeCount > 0 || alertCount > 0) && !open
                        && items.some(i => i.badge === 'audit' || i.badge === 'alerts') && (
                        <span className="ml-1 h-2 w-2 rounded-full bg-red-500" aria-hidden="true" />
                      )}
                      {constructionBadgeCount > 0 && !open && items.some(i => i.badge === 'construction') && (
                        <span className="ml-1 h-2 w-2 rounded-full bg-amber-400" aria-hidden="true" />
                      )}
                      <ChevronDownIcon className={`h-3 w-3 flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
                    </button>

                    {open && (
                      <div
                        id={group.id}
                        role="menu"
                        aria-label={`${group.label} menu`}
                        className="absolute left-0 mt-1 w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50"
                      >
                        <div className="py-1">{items.map(renderDropdownLink)}</div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Desktop right side: user dropdown + logout — hidden on mobile */}
            <div className="hidden lg:flex items-center gap-1">
              {/* User dropdown */}
              <div className="relative" ref={userMenuRef}>
                <button
                  ref={userMenuBtnRef}
                  onClick={() => setUserMenuOpen(o => !o)}
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                  aria-controls="user-menu"
                  className={`${
                    location.pathname.startsWith('/profile')
                      ? 'bg-primary-700 text-white'
                      : 'text-gray-300 hover:bg-primary-600 hover:text-white'
                  } flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors`}
                >
                  <UserCircleIcon className="h-5 w-5 flex-shrink-0" />
                  <span>{user?.full_name || user?.email}</span>
                  <ChevronDownIcon className={`h-3 w-3 flex-shrink-0 transition-transform duration-150 ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {userMenuOpen && (
                  <div
                    id="user-menu"
                    role="menu"
                    aria-label="User menu"
                    className="absolute right-0 mt-1 w-44 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50"
                  >
                    <div className="py-1">
                      {visibleTo(NAV_USER, isAdmin, isEmployee).map(renderDropdownLink)}
                      {/* About is not admin-only, but it used to live in the
                          admin Settings dropdown — so a non-admin had no way
                          to reach it on desktop at all. */}
                      <div className="border-t border-gray-100 my-1" />
                      {visibleTo(NAV_ABOUT, isAdmin, isEmployee).map(renderDropdownLink)}
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={handleLogout}
                aria-label="Log out"
                title="Log out"
                className="flex items-center gap-1.5 text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                <LogoutIcon className="h-5 w-5 flex-shrink-0" />
                <span>Logout</span>
              </button>
            </div>

            {/* Mobile right side: profile icon + hamburger */}
            <div className="flex lg:hidden items-center gap-1">
              <Link
                to="/profile"
                aria-label={`Profile: ${user?.full_name || user?.email}`}
                className={`${isActive('/profile')} p-2 rounded-md transition-colors`}
              >
                <UserCircleIcon className="h-6 w-6" />
              </Link>

              <button
                onClick={() => setMobileMenuOpen(o => !o)}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                className="p-2 rounded-md text-gray-300 hover:text-white hover:bg-primary-600 transition-colors"
              >
                <HamburgerIcon open={mobileMenuOpen} />
              </button>
            </div>

          </div>
        </div>

        {/* Mobile menu drawer — rendered from ./nav.tsx, the same source the
            desktop menus use, so the two can no longer drift apart. */}
        {mobileMenuOpen && (
          <div
            id="mobile-menu"
            ref={mobileMenuRef}
            className="lg:hidden border-t border-primary-700"
          >
            <div className="px-3 pt-2 pb-3 space-y-1">
              {visibleTo(NAV_PRIMARY, isAdmin, isEmployee).map(renderDrawerLink)}

              {/* One labelled block per group — same groups, same order and
                  same per-item visibility as the desktop dropdowns. */}
              {groups.map(group => (
                <div key={group.id} className="space-y-1">
                  <div className="border-t border-primary-700 my-1.5" />
                  <p className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {group.label}
                  </p>
                  {visibleTo(group.items, isAdmin, isEmployee).map(renderDrawerLink)}
                </div>
              ))}

              <div className="border-t border-primary-700 my-1.5" />
              {visibleTo(NAV_USER, isAdmin, isEmployee).map(renderDrawerLink)}

              <div className="border-t border-primary-700 my-1.5" />
              {visibleTo(NAV_ABOUT, isAdmin, isEmployee).map(renderDrawerLink)}

              <div className="border-t border-primary-700 my-1.5" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium text-gray-200 hover:bg-primary-600 hover:text-white transition-colors"
              >
                <LogoutIcon className="h-5 w-5 flex-shrink-0" />
                Log out
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Environment banner — hidden in production */}
      {environment && environment !== 'production' && (
        <div
          className="bg-amber-400 text-amber-900 text-center text-xs font-bold py-1 px-4 tracking-wide"
          role="status"
          aria-label={`Running in ${environment} environment`}
        >
          {environment.toUpperCase()} — not production
        </div>
      )}

      {/* Main content */}
      <main id="main-content" className="flex-1 max-w-7xl w-full mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Open critical/high security alerts, on every page, for admins, until one
              of them acknowledges it (app/services/security_alerts.py). */}
          {isAdmin && alertCount > 0 && !onPath('/admin/security-alerts') && (
            <div
              role="alert"
              className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              <span>
                <span className="font-semibold">
                  {alertCount} security alert{alertCount === 1 ? '' : 's'} need{alertCount === 1 ? 's' : ''} attention.
                </span>{' '}
                Every administrator was e-mailed; mark it handled once someone has looked.
              </span>
              <Link to="/admin/security-alerts" className="btn btn-sm bg-red-600 text-white hover:bg-red-700">
                Review
              </Link>
            </div>
          )}
          <Outlet />
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-400">
            <span>© 2026 Jesse Norell — FCC License Manager v{appVersion}</span>
            <span>Built with significant AI assistance</span>
          </div>
        </div>
      </footer>
    </div>
  )
}
